import { useReducedMotion } from "motion/react";
import type React from "react";
import { useEffect, useRef, useState } from "react";

import { cn } from "../../utils/cn";

/**
 * Motion mode an ambient illustration is in.
 *
 * - `running`: on screen, tab visible, motion allowed. The loop plays.
 * - `paused`: scrolled away or the tab is hidden. The loop holds its frame.
 * - `static`: the reader asked for reduced motion. The illustration shows
 *   its finished composition and never loops.
 */
export type AmbientMotion = "running" | "paused" | "static";

export interface AmbientIllustrationProps {
    /**
     * The illustration's own `@keyframes` and classes. Scope every selector
     * under a class unique to the illustration; this shell only sets
     * `data-motion` on the root and ships the shared pause/static rules.
     */
    css: string;
    /** The artwork, normally one `<svg>`. Hidden from assistive tech. */
    children: React.ReactNode;
    className?: string;
}

/**
 * Pauses or freezes every CSS animation under the root. Illustrations mark
 * the animated nodes with `data-ambient-motion`, so the rules never reach
 * into unrelated descendants. The static rule covers both the attribute
 * (driven by `useReducedMotion`) and the media query, so a first paint before
 * hydration is already still.
 */
const AMBIENT_CSS = `
[data-ambient-illustration][data-motion="paused"] [data-ambient-motion] { animation-play-state: paused; }
[data-ambient-illustration][data-motion="static"] [data-ambient-motion] { animation: none; }
@media (prefers-reduced-motion: reduce) {
    [data-ambient-illustration] [data-ambient-motion] { animation: none; }
}
`;

/**
 * Tracks whether an element is worth animating: in the viewport and in a
 * visible tab. Falls back to "yes" where `IntersectionObserver` is missing
 * (older runtimes, jsdom), which is the pre-extraction behaviour.
 */
function useOnScreen(ref: React.RefObject<HTMLElement | null>): boolean {
    const [onScreen, setOnScreen] = useState(true);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;
        let inView = true;
        const sync = (): void => setOnScreen(inView && !document.hidden);
        const observer =
            typeof IntersectionObserver === "undefined"
                ? undefined
                : new IntersectionObserver(([entry]) => {
                      inView = entry?.isIntersecting ?? true;
                      sync();
                  });
        observer?.observe(element);
        document.addEventListener("visibilitychange", sync);
        sync();
        return (): void => {
            observer?.disconnect();
            document.removeEventListener("visibilitychange", sync);
        };
    }, [ref]);

    return onScreen;
}

/**
 * `AmbientIllustration` — the mechanism behind a looping empty- or
 * error-state illustration: a decorative, `aria-hidden` frame that runs its
 * CSS loop only while it can be seen, and holds a still composition for
 * readers who prefer reduced motion.
 *
 * Extracted from the retail Stats empty states (`DistributionRewardsAnimation`
 * and `ReservesEmptyState`), which each hand-rolled the same
 * in-view + tab-visible + reduced-motion gate. The artwork and its keyframes
 * stay with the consumer: this component owns only when the loop may play.
 */
export function AmbientIllustration({
    css,
    children,
    className
}: AmbientIllustrationProps): React.ReactElement {
    const ref = useRef<HTMLDivElement>(null);
    const onScreen = useOnScreen(ref);
    const reducedMotion = useReducedMotion() ?? false;
    const motion: AmbientMotion = reducedMotion
        ? "static"
        : onScreen
          ? "running"
          : "paused";

    return (
        <div
            ref={ref}
            aria-hidden="true"
            data-ambient-illustration=""
            data-motion={motion}
            className={cn("pointer-events-none select-none", className)}
        >
            <style dangerouslySetInnerHTML={{ __html: AMBIENT_CSS + css }} />
            {children}
        </div>
    );
}
